import React, { useState, useEffect, useRef } from "react";
import {
  FaEnvelope,
  FaTimes,
  FaEllipsisH,
  FaMapMarkerAlt,
  FaUser,
  FaDownload,
} from "react-icons/fa";
import CustomInput from "../common/CustomInput";
import CustomPhoneInput from "../common/CustomPhoneInput";
import { businessCardService, searchService } from "../../services/api";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";

const ProfileCard = ({ disabled = false }) => {
  const navigate = useNavigate();

  // Profile card state
  const [showPreview, setShowPreview] = useState(false);
  const [showProfileCardEdit, setShowProfileCardEdit] = useState(false);
  const [isProfileCardSaving, setIsProfileCardSaving] = useState(false);
  const [profileCardFormData, setProfileCardFormData] = useState({
    name: "",
    phone_number: "",
    email: "",
    job_title: "",
    address: "",
    city: "",
    country: "",
    zipcode: "",
  });
  const [profileCardError, setProfileCardError] = useState("");
  const [profileCardId, setProfileCardId] = useState(null);
  const [originalProfileCardData, setOriginalProfileCardData] = useState({
    name: "",
    phone_number: "",
    email: "",
    job_title: "",
    address: "",
    city: "",
    country: "",
    zipcode: "",
  });
  const [countries, setCountries] = useState([]);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [selectedCountryData, setSelectedCountryData] = useState(null);
  const countryDropdownRef = useRef(null);
  const businessCardRef = useRef(null);

  // Filter countries based on search
  useEffect(() => {
    if (countrySearch.trim()) {
      const filtered = countries.filter((country) =>
        country.label.toLowerCase().includes(countrySearch.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [countrySearch, countries]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setIsCountryDropdownOpen(false);
        // Restore the selected country label if one exists
        if (profileCardFormData.country) {
          const selectedCountry = countries.find(
            (c) => c.value === profileCardFormData.country || c.label === profileCardFormData.country
          );
          if (selectedCountry) {
            setCountrySearch(selectedCountry.label);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileCardFormData.country, countries]);

  // Update country search when countries are loaded and there's a selected country
  useEffect(() => {
    if (countries.length > 0 && profileCardFormData.country) {
      const selectedCountry = countries.find(
        (c) => c.value === profileCardFormData.country || c.label === profileCardFormData.country
      );
      if (selectedCountry) {
        setCountrySearch(selectedCountry.label);
        setSelectedCountryData(selectedCountry);
      }
    }
  }, [countries, profileCardFormData.country]);

  // Fetch profile card data and countries on component mount
  useEffect(() => {
    const fetchProfileCard = async () => {
      try {
        const result = await businessCardService.getMyCard();
        if (
          result.success &&
          result.data &&
          Object.keys(result.data).length > 0
        ) {
          const cardData = {
            name: result.data.name || "",
            phone_number: result.data.phone_number || "",
            email: result.data.email || "",
            job_title: result.data.job_title || "",
            address: result.data.address || "",
            city: result.data.city || "",
            country: result.data.country || "",
            zipcode: result.data.zipcode || "",
          };
          setProfileCardFormData(cardData);
          setOriginalProfileCardData(cardData);
          setProfileCardId(result.data.id || null);

          // Set the country search value if country is selected
          if (result.data.country && countries.length > 0) {
            const selectedCountry = countries.find(
              (c) => c.value === result.data.country || c.label === result.data.country
            );
            if (selectedCountry) {
              setCountrySearch(selectedCountry.label);
              setSelectedCountryData(selectedCountry);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching profile card:", error);
      }
    };

    const fetchCountries = async () => {
      try {
        const result = await searchService.getCountries();
        if (result.success && result.data) {
          const countriesData = result.data.map((country) => ({
            value: country.name,
            label: country.name,
            id: country.id,
            image_url: country.image_url,
            alpha2code: country.alpha2code,
            alpha3code: country.alpha3code,
          }));
          setCountries(countriesData);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchProfileCard();
    fetchCountries();
  }, []);

  // Close preview when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showPreview &&
        !event.target.closest(".preview-popover") &&
        !event.target.closest(".preview-button")
      ) {
        setShowPreview(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPreview]);

  // Handle profile card field changes
  const handleProfileCardChange = (field, value) => {
    setProfileCardFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Safely read API error payloads and surface a user-friendly message
  const formatProfileCardError = (error) => {
    if (!error) {
      return "An unexpected error occurred while saving profile card";
    }

    if (typeof error === "string") {
      return error;
    }

    if (Array.isArray(error)) {
      return error.map((item) => formatProfileCardError(item)).join(" ");
    }

    if (typeof error === "object") {
      if (error.detail) {
        return formatProfileCardError(error.detail);
      }

      if (error.message) {
        return formatProfileCardError(error.message);
      }

      const firstKey = Object.keys(error)[0];
      if (firstKey) {
        return formatProfileCardError(error[firstKey]);
      }
    }

    return "An unexpected error occurred while saving profile card";
  };

  const handleProfileCardSave = async () => {
    setIsProfileCardSaving(true);
    setProfileCardError("");

    try {
      // Prepare payload for business card API
      const payload = {
        name: profileCardFormData.name,
        phone_number: profileCardFormData.phone_number,
        email: profileCardFormData.email,
        job_title: profileCardFormData.job_title,
        address: profileCardFormData.address,
        city: profileCardFormData.city,
        country: profileCardFormData.country,
        zipcode: profileCardFormData.zipcode,
      };

      let result;
      if (profileCardId) {
        // Update existing profile card
        result = await businessCardService.updateCard(profileCardId, payload);
      } else {
        // Create new profile card
        result = await businessCardService.createCard(payload);
      }

      if (result.success) {
        setProfileCardId(result.data.id);
        setOriginalProfileCardData({ ...profileCardFormData });
        setShowProfileCardEdit(false);
        setProfileCardError("");
        toast.success("Profile Card Saved");
      } else {
        const formattedError = formatProfileCardError(result.error);
        setProfileCardError(formattedError);
        toast.error(formattedError);
      }
    } catch (error) {
      console.error("Error saving profile card:", error);
      const formattedError = formatProfileCardError(error?.response?.data);
      setProfileCardError(formattedError);
      toast.error(formattedError);
    } finally {
      setIsProfileCardSaving(false);
    }
  };

  const handleProfileCardCancel = () => {
    // Reset profile card form data to the original profile card data
    setProfileCardFormData({ ...originalProfileCardData });
    setShowProfileCardEdit(false);
    setProfileCardError("");

    // Reset country search to the original selected country
    if (originalProfileCardData.country) {
      const selectedCountry = countries.find(
        (c) => c.value === originalProfileCardData.country || c.label === originalProfileCardData.country
      );
      if (selectedCountry) {
        setCountrySearch(selectedCountry.label);
        setSelectedCountryData(selectedCountry);
      } else {
        setCountrySearch("");
        setSelectedCountryData(null);
      }
    } else {
      setCountrySearch("");
      setSelectedCountryData(null);
    }
  };

  const handleDownloadProfileCard = async () => {
    if (!businessCardRef.current) return;

    try {
      // Hide the download button temporarily for the screenshot
      const downloadButton = businessCardRef.current.querySelector(
        ".download-profile-button"
      );
      if (downloadButton) {
        downloadButton.style.display = "none";
      }

      // Capture the business card as canvas
      const canvas = await html2canvas(businessCardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: false,
      });

      // Show the download button again
      if (downloadButton) {
        downloadButton.style.display = "block";
      }

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `profile-card-${
          profileCardFormData.name.replace(/\s+/g, "-").toLowerCase() ||
          "profile"
        }.png`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");

      toast.success("Profile card downloaded successfully!");
    } catch (error) {
      console.error("Error downloading profile card:", error);
      toast.error("Failed to download profile card");
    }
  };

  const phoneNumberDisplay = profileCardFormData.phone_number?.trim() || "Your number";
  const emailDisplay = profileCardFormData.email?.trim() || "your@email.com";
  const phoneIsPlaceholder = !profileCardFormData.phone_number?.trim();
  const emailIsPlaceholder = !profileCardFormData.email?.trim();

  return (
    <div className="bg-white p-6 border border-[#E6E6E6]">
      <div className="flex items-center justify-between gap-3 mb-6">
        <h2 className="text-[11px] font-bold text-[#3C3C3C]">
          YOUR PROFILE CARD
        </h2>
        {!showProfileCardEdit ? (
          <button
            className="ml-auto px-4 py-2 rounded-lg bg-[#2283FF] text-[#F5F5F5] text-sm"
            onClick={() => setShowProfileCardEdit(true)}
            aria-label="Edit profile card"
            disabled={disabled}
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-3 justify-end">
            <button
              className="border border-[#E6E6E6] bg-white text-[#1E1E1E] px-4 py-2 rounded-md text-sm transition-colors shadow-sm"
              onClick={handleProfileCardCancel}
              disabled={isProfileCardSaving}
            >
              Cancel
            </button>
            <button
              className="bg-[#2283FF] text-[#F5F5F5] px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-60"
              onClick={handleProfileCardSave}
              disabled={isProfileCardSaving}
            >
              {isProfileCardSaving ? "Saving..." : "Save changes"}
            </button>
          </div>
        )}
      </div>

      {/* Profile Card Error message */}
      {profileCardError && (
        <div className="mb-4 text-red-600 text-sm">{profileCardError}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CustomInput
          label="Name"
          value={profileCardFormData.name}
          onChange={(e) =>
            showProfileCardEdit
              ? handleProfileCardChange("name", e.target.value)
              : null
          }
          placeholder="Enter your name"
          disabled={!showProfileCardEdit}
        />

        <CustomPhoneInput
          label="Phone number"
          value={profileCardFormData.phone_number}
          onChange={(value) =>
            showProfileCardEdit
              ? handleProfileCardChange("phone_number", value)
              : null
          }
          placeholder="(91) 123-4567"
          country="es"
          className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
          disabled={!showProfileCardEdit}
        />

        <CustomInput
          label="Email address"
          type="email"
          value={profileCardFormData.email}
          onChange={(e) =>
            showProfileCardEdit
              ? handleProfileCardChange("email", e.target.value)
              : null
          }
          placeholder="Enter email address"
          icon={<FaEnvelope className="text-gray-400" />}
          disabled={!showProfileCardEdit}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CustomInput
          label="Job Title"
          value={profileCardFormData.job_title}
          onChange={(e) =>
            showProfileCardEdit
              ? handleProfileCardChange("job_title", e.target.value)
              : null
          }
          placeholder="Enter job title"
          disabled={!showProfileCardEdit}
        />
      </div>

      <div>
        <CustomInput
          label="Address"
          value={profileCardFormData.address}
          onChange={(e) =>
            showProfileCardEdit
              ? handleProfileCardChange("address", e.target.value)
              : null
          }
          placeholder="Enter full address"
          icon={<FaMapMarkerAlt className="text-gray-400" />}
          className="col-span-2"
          disabled={!showProfileCardEdit}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CustomInput
          label="City"
          value={profileCardFormData.city}
          onChange={(e) =>
            showProfileCardEdit
              ? handleProfileCardChange("city", e.target.value)
              : null
          }
          placeholder="Enter city"
          disabled={!showProfileCardEdit}
        />

        <div className="flex flex-col gap-2" ref={countryDropdownRef}>
          <label className="block text-sm font-bold text-[#1A212F]">
            Country
          </label>
          <div className="relative">
            <div className="relative">
              {selectedCountryData && !showProfileCardEdit && (
                <img
                  src={selectedCountryData.image_url}
                  alt={selectedCountryData.label}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-4 object-cover rounded-sm"
                />
              )}
              {selectedCountryData && showProfileCardEdit && (
                <img
                  src={selectedCountryData.image_url}
                  alt={selectedCountryData.label}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-4 object-cover rounded-sm"
                />
              )}
              <input
                type="text"
                className={`w-full ${selectedCountryData ? 'pl-10' : 'pl-3'} pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-[#F8F9FB] text-[#1A212FB2] text-[14px] placeholder:text-[14px] font-medium placeholder:font-medium placeholder:text-[#1A212F66]`}
                placeholder="Select country"
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setIsCountryDropdownOpen(true);
                }}
                onClick={() => showProfileCardEdit && setIsCountryDropdownOpen(true)}
                disabled={!showProfileCardEdit}
                readOnly={!showProfileCardEdit}
              />
              {showProfileCardEdit && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
                  onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                >
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                      isCountryDropdownOpen ? "transform rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              )}
            </div>

            {isCountryDropdownOpen && showProfileCardEdit && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.id}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3"
                      onClick={() => {
                        handleProfileCardChange("country", country.value);
                        setCountrySearch(country.label);
                        setSelectedCountryData(country);
                        setIsCountryDropdownOpen(false);
                      }}
                    >
                      <img
                        src={country.image_url}
                        alt={country.label}
                        className="w-6 h-4 object-cover rounded-sm"
                      />
                      <span>{country.label}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    No countries found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <CustomInput
          label="Zipcode"
          value={profileCardFormData.zipcode}
          onChange={(e) =>
            showProfileCardEdit
              ? handleProfileCardChange("zipcode", e.target.value)
              : null
          }
          placeholder="Enter zipcode"
          disabled={!showProfileCardEdit}
        />
      </div>

      {/* Profile Card Links */}
      <div className="mt-6 flex gap-4 relative">
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-[#2283FF] text-sm font-medium preview-button"
        >
          Preview profile card
        </button>
        <button
          onClick={() => navigate("/contacts")}
          type="button"
          className="text-[#2283FF] text-sm font-medium"
        >
          View all contacts
        </button>

        {/* Business Card Preview Popover */}
        <div
          className={`${
            showPreview
              ? "absolute bottom-[30px] left-0 z-50 w-full sm:w-[400px] bg-[#1A212F] rounded-lg shadow-2xl p-6 transform translate-y-0 opacity-100 transition-all duration-300 ease-out preview-popover border border-[#D6DBEA]"
              : "absolute bottom-[30px] left-0 z-50 w-[400px] bg-[#1A212F] rounded-lg shadow-2xl p-6 transform translate-y-4 opacity-0 pointer-events-none transition-all duration-300 ease-out preview-popover border border-[#D6DBEA]"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white text-lg font-bold">Your profile card</h3>
            <button
              onClick={() => setShowPreview(false)}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {/* Business Card */}
          <div
            ref={businessCardRef}
            className="bg-white rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 ">
                {/* Profile Image */}
                <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                  <FaUser className="text-gray-600 text-lg" />
                </div>
                {/* Name */}
                <h4 className="text-lg font-medium text-[#242426]">
                  {profileCardFormData.name || "Your Name"}
                </h4>
              </div>
              {/* Download button */}
              <button
                onClick={handleDownloadProfileCard}
                className="download-profile-button flex items-center gap-2 px-3 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                title="Download profile card as PNG"
              >
                <FaDownload className="w-3 h-3" />
              </button>
            </div>

            {/* Contact Information */}
            <div className="space-y-3 bg-[#F7F7F7] py-2 px-4 rounded-lg">
              <div className="flex items-center gap-3">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5257 2.5C8.01407 2.59528 8.46289 2.83413 8.81473 3.18597C9.16657 3.53781 9.40542 3.98663 9.5007 4.475M7.5257 0.5C8.54034 0.612718 9.48649 1.06709 10.2088 1.7885C10.9311 2.50992 11.3867 3.4555 11.5007 4.47M11.0007 8.46V9.96C11.0013 10.0993 10.9727 10.2371 10.917 10.3647C10.8612 10.4923 10.7793 10.6068 10.6767 10.7009C10.5741 10.7951 10.453 10.8667 10.3211 10.9114C10.1892 10.956 10.0494 10.9725 9.9107 10.96C8.37212 10.7928 6.8942 10.2671 5.5957 9.425C4.38761 8.65733 3.36337 7.63309 2.5957 6.425C1.75069 5.1206 1.22482 3.6355 1.0607 2.09C1.0482 1.95173 1.06464 1.81238 1.10895 1.68081C1.15326 1.54924 1.22448 1.42834 1.31808 1.32581C1.41168 1.22328 1.5256 1.14135 1.65259 1.08526C1.77959 1.02917 1.91687 1.00013 2.0557 1H3.5557C3.79835 0.997612 4.03359 1.08354 4.21758 1.24177C4.40156 1.39999 4.52174 1.61972 4.5557 1.86C4.61901 2.34003 4.73642 2.81136 4.9057 3.265C4.97297 3.44396 4.98753 3.63846 4.94765 3.82544C4.90777 4.01242 4.81513 4.18406 4.6807 4.32L4.0457 4.955C4.75748 6.20677 5.79393 7.24322 7.0457 7.955L7.6807 7.32C7.81664 7.18557 7.98828 7.09292 8.17526 7.05305C8.36224 7.01317 8.55674 7.02773 8.7357 7.095C9.18934 7.26428 9.66067 7.38169 10.1407 7.445C10.3836 7.47926 10.6054 7.6016 10.764 7.78875C10.9225 7.97589 11.0068 8.21479 11.0007 8.46Z"
                    stroke="#1E1E1E"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span
                  className={`text-[#222A35] text-xs ${
                    phoneIsPlaceholder ? "opacity-70 italic" : ""
                  }`}
                >
                  {phoneNumberDisplay}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <svg
                  width="12"
                  height="10"
                  viewBox="0 0 12 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 2C11 1.45 10.55 1 10 1H2C1.45 1 1 1.45 1 2M11 2V8C11 8.55 10.55 9 10 9H2C1.45 9 1 8.55 1 8V2M11 2L6 5.5L1 2"
                    stroke="#1E1E1E"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span
                  className={`text-[#222A35] text-xs ${
                    emailIsPlaceholder ? "opacity-70 italic" : ""
                  }`}
                >
                  {emailDisplay}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
